import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputLabel, MenuItem, Select, Switch, Table,
  TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField,
  Typography, makeStyles,
} from '@material-ui/core';
import { hostname } from '../providers/api';
import { formatDate } from './commercialFormatters';
import {
  CommercialEmpty, CommercialError, CommercialLoading,
} from './CommercialUi';
import {
  automationCodes, buildTemplatePreviewContext, getAutomationDefinition, getAutomationModeOptions,
  getAutomationPolicyMessage, getAutomationReadiness, getGovernanceDataPaths,
  getLatestAutomationRun, getWorkflowRetryDetails, hasUnsavedAutomationChanges,
} from './commercialAutomationState';
import { buildAuditChanges, formatAuditValue } from './commercialAuditState';
import {
  DEFAULT_HISTORY_ROWS_PER_PAGE,
  filterAuditHistory,
  filterWorkflowHistory,
  HISTORY_ROWS_PER_PAGE_OPTIONS,
  paginateHistory,
  uniqueHistoryValues,
} from './commercialHistoryState';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    minWidth: 0,
    overflow: 'hidden',
    width: '100%',
    '& .MuiTableContainer-root': { maxWidth: '100%', overflowX: 'auto' },
    '& table': { [theme.breakpoints.down('sm')]: { minWidth: 760 } },
  },
  sectionHeader: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
      '& button': { width: '100%' },
    },
  },
  editorGrid: {
    display: 'grid',
    gap: theme.spacing(3),
    gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
    paddingTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'minmax(0,1fr)' },
  },
  preview: {
    background: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    height: 420,
    width: '100%',
  },
  status: {
    background: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
  },
  automationList: { display: 'grid', gap: theme.spacing(2) },
  automationCard: { border: `1px solid ${theme.palette.divider}`, boxShadow: 'none' },
  automationHeader: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: { alignItems: 'stretch', flexDirection: 'column' },
  },
  automationHeading: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  automationGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: '1.1fr 1fr 1fr',
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'minmax(0,1fr)' },
  },
  automationColumn: {
    borderLeft: `2px solid ${theme.palette.divider}`,
    minWidth: 0,
    paddingLeft: theme.spacing(1.5),
  },
  compactList: {
    margin: theme.spacing(1, 0, 0),
    paddingLeft: theme.spacing(2.5),
    '& li': { marginBottom: theme.spacing(1) },
  },
  policy: {
    alignItems: 'center',
    background: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
  },
  controls: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
      '& > *': { width: '100%' },
    },
  },
  killSwitch: {
    border: `1px solid ${theme.palette.error.main}`,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
  },
  auditMeta: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: 'minmax(0,1fr)' },
  },
  auditValue: {
    fontFamily: 'monospace',
    maxWidth: 300,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  historyToolbar: {
    alignItems: 'start',
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: 'minmax(220px,2fr) repeat(2,minmax(150px,1fr))',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'repeat(2,minmax(0,1fr))' },
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: 'minmax(0,1fr)' },
  },
  workflowToolbar: {
    alignItems: 'start',
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: 'repeat(2,minmax(180px,260px))',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: 'minmax(0,1fr)' },
  },
  historySummary: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    minHeight: 36,
    [theme.breakpoints.down('xs')]: { alignItems: 'stretch', flexDirection: 'column' },
  },
  historyPagination: { borderTop: `1px solid ${theme.palette.divider}`, overflowX: 'auto' },
}));

function WorkflowRunHistory({ runs, onRetry }) {
  const classes = useStyles();
  const [selectedRun, setSelectedRun] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ATTENTION');
  const [workflowFilter, setWorkflowFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_HISTORY_ROWS_PER_PAGE);
  const workflows = runs.filter(
    run => automationCodes.includes(run.code) && run.code !== 'GLOBAL_KILL_SWITCH',
  );
  const filteredWorkflows = filterWorkflowHistory(workflows, {
    status: statusFilter,
    code: workflowFilter,
  });
  const pagination = paginateHistory(filteredWorkflows, page, rowsPerPage);
  const statusOptions = uniqueHistoryValues(workflows, 'status');
  const workflowOptions = uniqueHistoryValues(workflows, 'code');
  const changeStatusFilter = value => {
    setStatusFilter(value);
    setPage(0);
  };
  const changeWorkflowFilter = value => {
    setWorkflowFilter(value);
    setPage(0);
  };
  const showAllRuns = () => {
    setStatusFilter('ALL');
    setWorkflowFilter('ALL');
    setPage(0);
  };
  if (!workflows.length) {
    return <CommercialEmpty
      title="No workflow runs yet"
      description="Complete an action in Sales, Customer detail, or Agreements, or wait for an enabled automatic trigger."
    />;
  }
  const sourceHref = run => (
    run.entityType === 'CUSTOMER'
      ? `#/commercial/${run.entityId}/show`
      : run.entityType === 'OPPORTUNITY'
        ? '#/commercial-sales'
        : '#/commercial-renewals'
  );
  const selectedRecovery = selectedRun ? getWorkflowRetryDetails(selectedRun) : null;
  const confirmRetry = async () => {
    if (!selectedRun || !selectedRecovery) return;
    setRetrying(true);
    try {
      const result = await onRetry(selectedRun);
      setFeedback({
        runId: selectedRun.id,
        error: false,
        message: `Retry ${String(result.status || 'started').toLowerCase()}. Fresh credentials were sent to ${selectedRecovery.recipients.join(', ')}.`,
      });
      setSelectedRun(null);
    } catch (cause) {
      setFeedback({
        runId: selectedRun.id,
        error: true,
        message: cause.message || 'Credential delivery could not be retried.',
      });
      setSelectedRun(null);
    } finally {
      setRetrying(false);
    }
  };
  return <>
    <Box className={classes.workflowToolbar}>
      <TextField
        select
        fullWidth
        size="small"
        variant="outlined"
        label="Status"
        value={statusFilter}
        onChange={event => changeStatusFilter(event.target.value)}
        SelectProps={{ native: true, inputProps: { 'aria-label': 'Workflow status' } }}
      >
        <option value="ATTENTION">Needs attention</option>
        <option value="ALL">All statuses</option>
        {statusOptions.map(status => <option value={status} key={status}>
          {status.replaceAll('_', ' ')}
        </option>)}
      </TextField>
      <TextField
        select
        fullWidth
        size="small"
        variant="outlined"
        label="Workflow"
        value={workflowFilter}
        onChange={event => changeWorkflowFilter(event.target.value)}
        SelectProps={{ native: true, inputProps: { 'aria-label': 'Workflow type' } }}
      >
        <option value="ALL">All workflows</option>
        {workflowOptions.map(code => <option value={code} key={code}>
          {getAutomationDefinition(code).label}
        </option>)}
      </TextField>
    </Box>
    <Box className={classes.historySummary}>
      <div>
        <Typography variant="body2" role="status" aria-live="polite">
          {filteredWorkflows.length} of {workflows.length} workflow runs match
        </Typography>
        {statusFilter === 'ATTENTION' && <Typography variant="caption" color="textSecondary">
          Failed, blocked, approval-waiting, and unfinished runs stay visible first.
        </Typography>}
      </div>
      {(statusFilter !== 'ATTENTION' || workflowFilter !== 'ALL') && <Button size="small" onClick={() => {
        setStatusFilter('ATTENTION');
        setWorkflowFilter('ALL');
        setPage(0);
      }}>Reset filters</Button>}
    </Box>
    {filteredWorkflows.length ? <>
    <TableContainer><Table size="small" aria-label="Workflow run history">
      <TableHead><TableRow>
        <TableCell>Date and workflow</TableCell>
        <TableCell>Business source</TableCell>
        <TableCell>Recorded mode</TableCell>
        <TableCell>Status</TableCell>
        <TableCell>Step results</TableCell>
        <TableCell>Recovery</TableCell>
      </TableRow></TableHead>
      <TableBody>{pagination.rows.map(run => {
        const recovery = getWorkflowRetryDetails(run);
        const runFeedback = feedback?.runId === run.id ? feedback : null;
        return <TableRow key={run.id} hover>
        <TableCell>
          <Typography variant="subtitle2">{getAutomationDefinition(run.code).label}</Typography>
          <Typography variant="caption" color="textSecondary">
            {formatDate(run.createdAt)} · {run.requester || 'System'}
          </Typography>
        </TableCell>
        <TableCell>
          <Button size="small" href={sourceHref(run)}>
            Open {run.entityType === 'OPPORTUNITY' ? 'Sales' : run.entityType === 'CUSTOMER' ? 'customer' : 'Agreements'}
          </Button>
        </TableCell>
        <TableCell>{run.mode}</TableCell>
        <TableCell>
          <Chip
            size="small"
            color={['FAILED', 'BLOCKED'].includes(run.status) ? 'secondary' : run.status === 'COMPLETED' ? 'primary' : 'default'}
            label={run.status}
          />
          {run.error && <Typography variant="caption" color="error" display="block">{run.error}</Typography>}
        </TableCell>
        <TableCell>
          {run.steps?.length
            ? run.steps.map(step => <Chip
              key={step.id || step.code}
              size="small"
              variant="outlined"
              label={`${step.code.replaceAll('_', ' ')} · ${step.status}`}
              style={{ margin: 2 }}
            />)
            : <Typography variant="caption" color="textSecondary">No step results recorded</Typography>}
        </TableCell>
        <TableCell>
          {recovery
            ? <Button size="small" color="primary" onClick={() => setSelectedRun(run)}>
              Retry welcome email
            </Button>
            : <Typography variant="caption" color="textSecondary">No recovery action</Typography>}
          {runFeedback && <Typography
            variant="caption"
            color={runFeedback.error ? 'error' : 'textPrimary'}
            display="block"
            role="status"
          >
            {runFeedback.message}
          </Typography>}
        </TableCell>
      </TableRow>;
      })}</TableBody>
    </Table></TableContainer>
    <TablePagination
      className={classes.historyPagination}
      component="div"
      count={filteredWorkflows.length}
      page={pagination.page}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={HISTORY_ROWS_PER_PAGE_OPTIONS}
      labelRowsPerPage="Runs per page:"
      SelectProps={{ inputProps: { 'aria-label': 'Workflow rows per page' } }}
      onPageChange={(_event, nextPage) => setPage(nextPage)}
      onRowsPerPageChange={event => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
      }}
    />
    </> : <CommercialEmpty
      title={statusFilter === 'ATTENTION' ? 'No workflow runs need attention' : 'No workflow runs match these filters'}
      description="Change the status or workflow filter to inspect other recorded outcomes."
      action={<Button color="primary" onClick={showAllRuns}>Show all runs</Button>}
    />}
    <Dialog
      open={Boolean(selectedRun)}
      onClose={() => !retrying && setSelectedRun(null)}
      aria-labelledby="credential-retry-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="credential-retry-title">Retry credential delivery?</DialogTitle>
      <DialogContent dividers>
        <Typography paragraph>{selectedRecovery?.description}</Typography>
        <Typography variant="body2" color="textSecondary">
          Confirming rotates the existing password before sending. The temporary password is included only in the welcome email; it will not appear in workflow history, audit data, or this screen.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSelectedRun(null)} disabled={retrying}>Cancel</Button>
        <Button color="primary" variant="contained" onClick={confirmRetry} disabled={retrying}>
          {retrying ? 'Retrying...' : 'Rotate password and resend'}
        </Button>
      </DialogActions>
    </Dialog>
  </>;
}

const governedSwitchCodes = new Set(['CLIENT_INTAKE_EMAIL', 'HUBSPOT_SYNC', 'HOLDED_SYNC']);
const contextualSetting = source => {
  const modeOptions = getAutomationModeOptions(source.code);
  const validMode = modeOptions.some(option => option.value === source.mode)
    ? source.mode
    : modeOptions[0].value;
  return { ...source, enabled: governedSwitchCodes.has(source.code) ? Boolean(source.enabled) : true, mode: validMode, configuration: source.configuration || {} };
};

function ContextualAutomationControlCard({
  classes, source, runs, templates, emergencyStopActive, onSave,
}) {
  const [value, setValue] = useState(contextualSetting(source));
  const [saving, setSaving] = useState(false);
  useEffect(() => setValue(contextualSetting(source)), [source]);

  const definition = getAutomationDefinition(source.code);
  const readiness = getAutomationReadiness(source.code);
  const modeOptions = getAutomationModeOptions(source.code);
  const contextualOnly = modeOptions.length === 1;
  const dirty = !contextualOnly
    && hasUnsavedAutomationChanges(contextualSetting(source), value);
  const latest = getLatestAutomationRun(source.code, runs);
  const requiredTemplate = source.code === 'RENEWAL_OUTREACH' ? 'RENEWAL' : source.code === 'CLIENT_INTAKE_EMAIL' ? 'CLIENT_INTAKE' : ['HUBSPOT_SYNC','HOLDED_SYNC'].includes(source.code) ? null : 'ONBOARDING';
  const activeTemplate = templates.find(
    template => template.code === requiredTemplate && template.active,
  );
  const manualTriggers = definition.triggers.filter(trigger => trigger.type.includes('MANUAL'));
  const automaticTriggers = definition.triggers.filter(trigger => trigger.type.includes('AUTOMATIC'));
  const save = async () => {
    setSaving(true);
    await onSave({ ...value, enabled: governedSwitchCodes.has(source.code) ? value.enabled : true });
    setSaving(false);
  };
  const triggerList = items => <ul className={classes.compactList}>
    {items.map(trigger => <li key={trigger.code}>
      <Typography variant="subtitle2">{trigger.label}</Typography>
      <Typography variant="body2" color="textSecondary">{trigger.detail}</Typography>
    </li>)}
  </ul>;

  return <Card className={classes.automationCard} variant="outlined">
    <CardContent>
      <Box className={classes.automationHeader}>
        <Box>
          <Box className={classes.automationHeading}>
            <Typography variant="h6" component="h3">{definition.label}</Typography>
            <Chip size="small" color="primary" label={readiness.label} />
            {dirty && <Chip size="small" label="UNSAVED CHANGE" />}
          </Box>
          <Typography variant="body2" color="textSecondary">{definition.action}</Typography>
        </Box>
        <Box textAlign="right">
          <Typography variant="overline" color="textSecondary">LAST RUN</Typography>
          <Typography variant="body2">
            {latest ? `${formatDate(latest.createdAt)} · ${latest.status}` : 'Never run'}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.automationGrid}>
        <Box className={classes.automationColumn}>
          <Typography variant="overline" color="textSecondary">WHERE A USER STARTS IT</Typography>
          {triggerList(manualTriggers)}
        </Box>
        <Box className={classes.automationColumn}>
          <Typography variant="overline" color="textSecondary">SYSTEM TRIGGER</Typography>
          {automaticTriggers.length
            ? triggerList(automaticTriggers)
            : <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              None. This action only runs after a user reviews it on Customer detail.
            </Typography>}
        </Box>
        <Box className={classes.automationColumn}>
          <Typography variant="overline" color="textSecondary">REQUIRED BEFORE IT CAN RUN</Typography>
          <ul className={classes.compactList}>
            {definition.prerequisites.map(item => <li key={item}>
              <Typography variant="body2">{item}</Typography>
            </li>)}
          </ul>
          {requiredTemplate && <Chip
            size="small"
            color={activeTemplate ? 'primary' : 'secondary'}
            label={activeTemplate
              ? `${requiredTemplate} TEMPLATE READY · V${activeTemplate.version}`
              : `${requiredTemplate} TEMPLATE MISSING`}
          />}
        </Box>
      </Box>

      <Box className={classes.policy}>
        <Box>
          <Typography variant="overline" color="textSecondary">
            {dirty ? 'AFTER YOU SAVE' : 'CURRENT BEHAVIOUR'}
          </Typography>
          <Typography variant="body2">
            <strong>{getAutomationPolicyMessage(value, emergencyStopActive, source.code)}</strong>
          </Typography>
          <Typography variant="caption" color="textSecondary">{definition.limitation}</Typography>
        </Box>
        <Box className={classes.controls}>
          {governedSwitchCodes.has(source.code) && <Box display="flex" alignItems="center">
            <Switch checked={Boolean(value.enabled)} onChange={event => setValue({ ...value, enabled: event.target.checked })} inputProps={{ 'aria-label': `Enable ${definition.label}` }} />
            <Typography variant="body2">{value.enabled ? 'Enabled' : 'Disabled'}</Typography>
          </Box>}
          {contextualOnly
            ? <>
              <Chip color="primary" variant="outlined" label="Always contextual" />
              <Button color="primary" href="#/commercial-customers">Open customers</Button>
            </>
            : <>
              <TextField
                select
                size="small"
                variant="outlined"
                label="Execution mode"
                value={value.mode}
                onChange={event => setValue({ ...value, mode: event.target.value })}
                SelectProps={{ inputProps: { 'aria-label': `Execution mode for ${definition.label}` } }}
                style={{ minWidth: 180 }}
              >
                {modeOptions.map(option => <MenuItem value={option.value} key={option.value}>
                  {option.label}
                </MenuItem>)}
              </TextField>
              <Button
                color="primary"
                variant="contained"
                disabled={!dirty || saving}
                onClick={save}
              >
                {saving ? 'Saving…' : 'Save mode'}
              </Button>
            </>}
        </Box>
      </Box>
      {source.code === 'CLIENT_INTAKE_EMAIL' && <Box mt={2} display="grid" gridGap={12} gridTemplateColumns="repeat(auto-fit,minmax(220px,1fr))">
        <Box display="flex" alignItems="center"><Switch checked={value.configuration?.templateApproved === true} onChange={event => setValue({ ...value, configuration: { ...value.configuration, templateApproved: event.target.checked } })} inputProps={{ 'aria-label': 'Client intake template approved' }}/><Typography variant="body2">Template approved</Typography></Box>
        <Box display="flex" alignItems="center"><Switch checked={value.configuration?.senderVerified === true} onChange={event => setValue({ ...value, configuration: { ...value.configuration, senderVerified: event.target.checked } })} inputProps={{ 'aria-label': 'Client intake sender verified' }}/><Typography variant="body2">Sender verified</Typography></Box>
        <TextField size="small" variant="outlined" label="Pilot recipient allowlist" helperText="Comma-separated; leave empty only for the reviewed full rollout." value={(value.configuration?.allowlist || []).join(', ')} onChange={event => setValue({ ...value, configuration: { ...value.configuration, allowlist: event.target.value.split(',').map(item => item.trim().toLowerCase()).filter(Boolean) } })}/>
      </Box>}
    </CardContent>
  </Card>;
}

export default function CommercialGovernancePanel({ token, section }) {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [revision, setRevision] = useState(0);
  const [templateForm, setTemplateForm] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewDetails, setPreviewDetails] = useState({});
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [message, setMessage] = useState(null);
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  useEffect(() => {
    let active = true;
    const requestHeaders = { Authorization: `Bearer ${token}` };
    const paths = getGovernanceDataPaths(section);
    setData(null);
    setError(null);
    Promise.all(paths.map(path => fetch(`${hostname}/commercial/${path}`, {
      headers: requestHeaders,
    }).then(response => (
      response.ok
        ? response.json()
        : Promise.reject(new Error(`${path} failed (${response.status})`))
    ))))
      .then(results => {
        if (!active) return;
        const next = {};
        paths.forEach((path, index) => {
          if (path === 'automation-workspace') next.automation = results[index];
          if (path === 'customers') next.customers = results[index];
          if (path === 'audit') next.audit = results[index];
        });
        setData(next);
      })
      .catch(cause => {
        if (active) setError(cause);
      });
    return () => { active = false; };
  }, [token, revision, section]);

  const previewCustomerId = templateForm?.testCustomerId || '';
  useEffect(() => {
    if (!previewCustomerId || previewDetails[previewCustomerId]) {
      setPreviewLoadingId(null);
      setPreviewError(null);
      return undefined;
    }
    let active = true;
    setPreviewLoadingId(previewCustomerId);
    setPreviewError(null);
    fetch(`${hostname}/commercial/customers/${previewCustomerId}/detail?range=6`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(response => (
      response.ok
        ? response.json()
        : Promise.reject(new Error(`Customer preview failed (${response.status})`))
    )).then(detail => {
      if (!active) return;
      setPreviewDetails(current => ({ ...current, [previewCustomerId]: detail }));
      setPreviewLoadingId(null);
    }).catch(cause => {
      if (!active) return;
      setPreviewLoadingId(null);
      setPreviewError(cause.message || 'Customer preview data could not be loaded.');
    });
    return () => { active = false; };
  }, [previewCustomerId, previewDetails, token]);

  if (error) {
    return <CommercialError
      title="This operations page could not be loaded"
      error={error}
      onRetry={() => setRevision(value => value + 1)}
    />;
  }
  if (!data) return <CommercialLoading label="Loading Commercial operations…" />;

  const update = async setting => {
    try {
      const response = await fetch(
        `${hostname}/commercial/automations/${setting.code}`,
        { method: 'PATCH', headers, body: JSON.stringify(setting) },
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        setMessage(result.error || `Could not save ${setting.code.replaceAll('_', ' ').toLowerCase()}`);
        return false;
      }
      setMessage(`${getAutomationDefinition(setting.code).label} saved as ${setting.mode}`);
      setRevision(value => value + 1);
      return true;
    } catch (cause) {
      setMessage(cause.message || 'The workflow rule could not be saved.');
      return false;
    }
  };

  const saveTemplate = async () => {
    try {
      const payload = { ...templateForm, ccRecipients: templateForm.ccRecipients || [] };
      const response = await fetch(`${hostname}/commercial/message-templates`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        setMessage(result.error || 'The email template could not be saved.');
        return;
      }
      setMessage(`${payload.code} template v${result.version} is now active`);
      setTemplateForm(null);
      setRevision(value => value + 1);
    } catch (cause) {
      setMessage(cause.message || 'The email template could not be saved.');
    }
  };

  const retryAutomationRun = async run => {
    const response = await fetch(`${hostname}/commercial/automation-runs/${run.id}/retry`, {
      method: 'POST',
      headers,
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(result.message || result.error || 'Credential delivery could not be retried.');
    }
    if (result.status !== 'COMPLETED') {
      throw new Error(result.error || result.message || `Credential delivery ended as ${String(result.status || 'unknown').toLowerCase()}.`);
    }
    return result;
  };

  const automation = data.automation || { settings: [], runs: [], templates: [] };
  const globalSettings = (automation.settings || []).filter(setting => !setting.accountId);
  const killSwitch = globalSettings.find(setting => setting.code === 'GLOBAL_KILL_SWITCH');
  const operationalSettings = globalSettings.filter(setting => (
    setting.code !== 'GLOBAL_KILL_SWITCH'
    && !setting.configuration?.legacy
    && automationCodes.includes(setting.code)
  ));
  const emergencyStopActive = Boolean(killSwitch?.enabled);

  return <Card className={classes.root}>
    <CardContent>
      {message && <Box className={classes.status} role="status">
        <Typography variant="body2">{message}</Typography>
      </Box>}

      {section === 'automation' && <>
        <Box className={classes.sectionHeader}>
          <Box>
            <Typography variant="h6">Choose how each workflow starts</Typography>
            <Typography variant="body2" color="textSecondary">
              This page only chooses behaviour. Work starts from the Sales, Customer, or Agreements page named on each rule.
            </Typography>
          </Box>
        </Box>
        <Box className={classes.automationList}>
          {operationalSettings.map(setting => <ContextualAutomationControlCard
            key={setting.id}
            classes={classes}
            source={setting}
            runs={automation.runs || []}
            templates={automation.templates || []}
            emergencyStopActive={emergencyStopActive}
            onSave={update}
          />)}
        </Box>
        {killSwitch && <KillSwitchControl
          classes={classes}
          source={killSwitch}
          onSave={update}
        />}
      </>}

      {section === 'templates' && <>
        <Box className={classes.sectionHeader}>
          <Box>
            <Typography variant="h6">Template versions</Typography>
            <Typography variant="body2" color="textSecondary">
              Edit custom HTML and preview it with a real customer before making a new version active.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setTemplateForm(blankTemplate)}
          >
            Create email template
          </Button>
        </Box>
        <TemplateTable
          templates={automation.templates || []}
          onPreview={setPreviewTemplate}
          onEdit={template => setTemplateForm({
            ...template,
            ccRecipients: template.ccRecipients || [],
            testCustomerId: '',
          })}
        />
        {templateForm && <TemplateEditor
          value={templateForm}
          customers={data.customers || []}
          previewContext={previewCustomerId && previewDetails[previewCustomerId]
            ? buildTemplatePreviewContext(
              (data.customers || []).find(item => item.id === previewCustomerId),
              previewDetails[previewCustomerId],
            )
            : null}
          previewLoading={previewLoadingId === previewCustomerId}
          previewError={previewCustomerId ? previewError : null}
          onChange={setTemplateForm}
          onClose={() => setTemplateForm(null)}
          onSave={saveTemplate}
        />}
        {previewTemplate && <SavedTemplatePreview
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />}
      </>}

      {section === 'history' && <>
        <Typography variant="h6">Runs that need review</Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Read-only outcomes for actions started from a business page or by a real automatic trigger.
        </Typography>
        <WorkflowRunHistory runs={automation.runs || []} onRetry={retryAutomationRun} />
      </>}

      {section === 'audit' && <AuditTable rows={data.audit || []} />}
    </CardContent>
  </Card>;
}

const blankTemplate = {
  code: 'ONBOARDING',
  subject: 'Welcome to Bcome, {{company}}',
  body: '<div style="font-family:Arial,sans-serif;max-width:600px;margin:auto"><h1>Welcome to Bcome</h1><p>Hello {{name}},</p><p>We are delighted to welcome {{company}}.</p></div>',
  ccRecipients: [],
  testCustomerId: '',
};

function KillSwitchControl({ classes, source, onSave }) {
  const [value, setValue] = useState(source);
  const [saving, setSaving] = useState(false);
  useEffect(() => setValue(source), [source]);
  const dirty = hasUnsavedAutomationChanges(source, value);
  const definition = getAutomationDefinition(source.code);
  const save = async () => {
    setSaving(true);
    await onSave(value);
    setSaving(false);
  };
  return <Box className={classes.killSwitch}>
    <Box className={classes.automationHeader}>
      <Box>
        <Box className={classes.automationHeading}>
          <Typography variant="h6">{definition.label}</Typography>
          <Chip
            size="small"
            color={value.enabled ? 'secondary' : 'default'}
            label={value.enabled ? 'EMERGENCY STOP ACTIVE' : 'WORKFLOWS ALLOWED'}
          />
          {dirty && <Chip size="small" label="UNSAVED CHANGE" />}
        </Box>
        <Typography variant="body2" color="textSecondary">{definition.action}</Typography>
      </Box>
      <Box display="flex" alignItems="center">
        <Switch
          checked={value.enabled}
          color="secondary"
          inputProps={{ 'aria-label': 'Activate global emergency stop' }}
          onChange={event => setValue({ ...value, enabled: event.target.checked })}
        />
        <Typography variant="body2">Stop new runs</Typography>
      </Box>
    </Box>
    <Box className={classes.policy}>
      <Typography variant="body2">
        This takes effect as soon as it is saved and blocks both contextual and scheduled runs.
      </Typography>
      <Button
        color="secondary"
        variant="contained"
        disabled={!dirty || saving}
        onClick={save}
      >
        {saving ? 'Saving…' : 'Save emergency control'}
      </Button>
    </Box>
  </Box>;
}

function TemplateTable({ templates, onEdit, onPreview }) {
  if (!templates.length) {
    return <CommercialEmpty
      title="No email templates"
      description="Onboarding and renewal messages need an active template before they can be sent."
    />;
  }
  return <TableContainer>
    <Table size="small">
      <TableHead><TableRow>
        <TableCell>Template</TableCell>
        <TableCell>Version</TableCell>
        <TableCell>Subject</TableCell>
        <TableCell>CC</TableCell>
        <TableCell>Status</TableCell>
        <TableCell />
      </TableRow></TableHead>
      <TableBody>{templates.map(template => <TableRow key={template.id}>
        <TableCell>{template.code}</TableCell>
        <TableCell>v{template.version}</TableCell>
        <TableCell>{template.subject}</TableCell>
        <TableCell>{(template.ccRecipients || []).join(', ') || 'None'}</TableCell>
        <TableCell>
          <Chip
            size="small"
            color={template.active ? 'primary' : 'default'}
            label={template.active ? 'ACTIVE' : 'INACTIVE'}
          />
        </TableCell>
        <TableCell>
          <Box display="flex" flexWrap="wrap">
          <Button
            aria-label={`Preview ${template.code} v${template.version}`}
            onClick={() => onPreview(template)}
          >
            Preview
          </Button>
          <Button onClick={() => onEdit(template)}>
            {template.active ? 'Edit as new version' : 'Use as base'}
          </Button>
          </Box>
        </TableCell>
      </TableRow>)}</TableBody>
    </Table>
  </TableContainer>;
}

function AuditReviewDialog({ row, onClose }) {
  const classes = useStyles();
  if (!row) return null;
  const changes = buildAuditChanges(row.before, row.after);
  const isCreate = row.before === null || row.before === undefined || row.before === 'null';
  const isDelete = row.after === null || row.after === undefined || row.after === 'null';
  return <Dialog
    open
    fullWidth
    maxWidth="md"
    onClose={onClose}
    aria-labelledby="commercial-audit-review-title"
  >
    <DialogTitle id="commercial-audit-review-title">Review commercial change</DialogTitle>
    <DialogContent>
      <Box className={classes.auditMeta}>
        <div><Typography variant="caption" color="textSecondary">Action</Typography><Typography>{row.action}</Typography></div>
        <div><Typography variant="caption" color="textSecondary">Entity</Typography><Typography>{row.entityType}{row.entityId ? ` \u00b7 ${row.entityId}` : ''}</Typography></div>
        <div><Typography variant="caption" color="textSecondary">Changed by</Typography><Typography>{row.actor || 'System'}</Typography></div>
        <div><Typography variant="caption" color="textSecondary">Changed on</Typography><Typography>{formatDate(row.createdAt)}</Typography></div>
      </Box>
      {isCreate && !isDelete && <Typography variant="body2" color="textSecondary" paragraph>
        This record was created; there is no previous state.
      </Typography>}
      {isDelete && !isCreate && <Typography variant="body2" color="textSecondary" paragraph>
        This record was deleted; there is no resulting state.
      </Typography>}
      {changes.length ? <TableContainer>
        <Table size="small" aria-label="Changed fields">
          <TableHead><TableRow>
            <TableCell>Field</TableCell>
            <TableCell>Before</TableCell>
            <TableCell>After</TableCell>
          </TableRow></TableHead>
          <TableBody>{changes.map(change => <TableRow key={change.field}>
            <TableCell component="th" scope="row">{change.field}</TableCell>
            <TableCell className={classes.auditValue}>{formatAuditValue(change.before)}</TableCell>
            <TableCell className={classes.auditValue}>{formatAuditValue(change.after)}</TableCell>
          </TableRow>)}</TableBody>
        </Table>
      </TableContainer> : <CommercialEmpty
        title="No field-level differences were recorded"
        description="The audit event records the action, but its saved before and after payloads are identical or empty."
      />}
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Close review</Button></DialogActions>
  </Dialog>;
}

function AuditTable({ rows }) {
  const classes = useStyles();
  const [selectedRow, setSelectedRow] = useState(null);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('ALL');
  const [entityFilter, setEntityFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_HISTORY_ROWS_PER_PAGE);
  const filteredRows = filterAuditHistory(rows, {
    search,
    action: actionFilter,
    entity: entityFilter,
  });
  const pagination = paginateHistory(filteredRows, page, rowsPerPage);
  const actionOptions = uniqueHistoryValues(rows, 'action');
  const entityOptions = uniqueHistoryValues(rows, 'entityType');
  const resetFilters = () => {
    setSearch('');
    setActionFilter('ALL');
    setEntityFilter('ALL');
    setPage(0);
  };
  return <>
    <Typography variant="h6">Recorded commercial changes</Typography>
    <Typography variant="body2" color="textSecondary" paragraph>
      Sensitive changes are recorded with their date, actor, action, and entity.
    </Typography>
    {rows.length
      ? <>
        <Box className={classes.historyToolbar}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Search"
            placeholder="Actor, action, entity, or ID"
            value={search}
            inputProps={{ 'aria-label': 'Search audit history' }}
            onChange={event => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            label="Action"
            value={actionFilter}
            SelectProps={{ native: true, inputProps: { 'aria-label': 'Audit action' } }}
            onChange={event => {
              setActionFilter(event.target.value);
              setPage(0);
            }}
          >
            <option value="ALL">All actions</option>
            {actionOptions.map(action => <option key={action} value={action}>{action}</option>)}
          </TextField>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            label="Entity"
            value={entityFilter}
            SelectProps={{ native: true, inputProps: { 'aria-label': 'Audit entity' } }}
            onChange={event => {
              setEntityFilter(event.target.value);
              setPage(0);
            }}
          >
            <option value="ALL">All entities</option>
            {entityOptions.map(entity => <option key={entity} value={entity}>{entity}</option>)}
          </TextField>
        </Box>
        <Box className={classes.historySummary}>
          <Typography variant="body2" role="status" aria-live="polite">
            {filteredRows.length} of {rows.length} audited changes match
          </Typography>
          {(search || actionFilter !== 'ALL' || entityFilter !== 'ALL') && <Button size="small" onClick={resetFilters}>
            Clear filters
          </Button>}
        </Box>
        {filteredRows.length ? <>
        <TableContainer><Table size="small" aria-label="Commercial audit history">
        <TableHead><TableRow>
          <TableCell>Date</TableCell>
          <TableCell>Actor</TableCell>
          <TableCell>Action</TableCell>
          <TableCell>Entity</TableCell>
          <TableCell>Details</TableCell>
        </TableRow></TableHead>
        <TableBody>{pagination.rows.map(row => <TableRow key={row.id}>
          <TableCell>{formatDate(row.createdAt)}</TableCell>
          <TableCell>{row.actor || 'System'}</TableCell>
          <TableCell>{row.action}</TableCell>
          <TableCell component="th" scope="row">{row.entityType}</TableCell>
          <TableCell><Button
            aria-label={`Review change for ${row.entityType} ${row.entityId || row.id}`}
            onClick={() => setSelectedRow(row)}
          >Review change</Button></TableCell>
        </TableRow>)}</TableBody>
        </Table></TableContainer>
        <TablePagination
          className={classes.historyPagination}
          component="div"
          count={filteredRows.length}
          page={pagination.page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={HISTORY_ROWS_PER_PAGE_OPTIONS}
          labelRowsPerPage="Changes per page:"
          SelectProps={{ inputProps: { 'aria-label': 'Audit rows per page' } }}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={event => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
        />
        </> : <CommercialEmpty
          title="No audited changes match these filters"
          description="Clear or adjust the search, action, and entity filters to see recorded changes."
          action={<Button color="primary" onClick={resetFilters}>Clear filters</Button>}
        />}
      </>
      : <CommercialEmpty
        title="No audited changes yet"
        description="Sensitive Commercial changes and workflow decisions will appear here."
      />}
    <AuditReviewDialog row={selectedRow} onClose={() => setSelectedRow(null)} />
  </>;
}

const escapeHtml = value => String(value ?? '').replace(
  /[&<>"']/g,
  character => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[character]),
);

const render = (source, customer, escape = true) => String(source || '').replace(
  /\{\{\s*(name|company|country|currentMrr|renewalDate|email|temporaryPassword|loginUrl|intakeUrl|expiryDate)\s*\}\}/g,
  (_match, key) => {
    const value = {
      name: customer?.name || 'Customer name',
      company: customer?.company || customer?.name || 'Company name',
      country: customer?.country || 'Country',
      currentMrr: customer?.currentMrr || '0',
      renewalDate: customer?.renewalDate || 'Renewal date',
      email: customer?.email || 'client@example.com',
      temporaryPassword: 'Example-Password-1!',
      loginUrl: 'https://app.bcome.biz',
      intakeUrl: 'https://admin.bcome.biz/#/client-intake/example-token',
      expiryDate: '30 September 2026',
    }[key];
    return escape ? escapeHtml(value) : value;
  },
);

const onboardingMarker = '<!-- BCOME_ONBOARDING_CREDENTIALS -->';
const onboardingBlock = '<section style="margin:30px 0 0;padding:22px;border-left:3px solid #8b2f4a;background:#f5f4f2"><h2 style="margin:0 0 16px;color:#0a0908;font-family:Arial,Helvetica,sans-serif;font-size:15px;letter-spacing:.8px;text-transform:uppercase">Your BCome access</h2><p style="margin:0 0 8px"><strong>Email:</strong> {{email}}</p><p style="margin:0 0 8px"><strong>Temporary password:</strong> {{temporaryPassword}}</p><p style="margin:0;color:#8b2f4a"><strong>Change this temporary password as soon as you sign in.</strong></p></section>';

const withRequiredTemplateContent = template => {
  if (template.code !== 'ONBOARDING') return template.body;
  return template.body.includes(onboardingMarker)
    ? template.body.replace(onboardingMarker, onboardingBlock)
    : `${template.body}${onboardingBlock}`;
};

function SavedTemplatePreview({ template, onClose }) {
  const classes = useStyles();
  const titleId = `saved-template-preview-${template.id}`;
  return <Dialog
    open
    fullWidth
    maxWidth="md"
    onClose={onClose}
    aria-labelledby={titleId}
  >
    <DialogTitle id={titleId}>Preview {template.code} v{template.version}</DialogTitle>
    <DialogContent>
      <Box display="flex" gridGap={8} alignItems="center" flexWrap="wrap" mb={2}>
        <Chip size="small" color={template.active ? 'primary' : 'default'} label={template.active ? 'ACTIVE' : 'INACTIVE'} />
        <Typography variant="caption" color="textSecondary">Example data is used for placeholders. This preview cannot send email.</Typography>
      </Box>
      <Typography variant="caption" color="textSecondary">SUBJECT</Typography>
      <Typography variant="subtitle1" gutterBottom>{render(template.subject, null, false)}</Typography>
      <iframe
        title="Saved email preview"
        srcDoc={render(withRequiredTemplateContent(template), null, true)}
        sandbox=""
        className={classes.preview}
      />
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Close preview</Button></DialogActions>
  </Dialog>;
}

function TemplateEditor({
  value, customers, previewContext, previewLoading, previewError,
  onChange, onClose, onSave,
}) {
  const classes = useStyles();
  const customer = value.testCustomerId ? previewContext : null;
  const previewBody = withRequiredTemplateContent(value);
  return <Dialog open fullWidth maxWidth="lg">
    <DialogTitle>
      {value.id ? `Edit ${value.code} v${value.version} as a new version` : 'Create email template'}
    </DialogTitle>
    <DialogContent>
      <Box className={classes.editorGrid}>
        <Box display="grid" gridGap={16}>
          <FormControl variant="outlined">
            <InputLabel>Message type</InputLabel>
            <Select
              label="Message type"
              value={value.code}
              onChange={event => onChange({ ...value, code: event.target.value })}
            >
              <MenuItem value="ONBOARDING">Onboarding</MenuItem>
              <MenuItem value="RENEWAL">Renewal</MenuItem>
              <MenuItem value="CLIENT_INTAKE">Client intake</MenuItem>
            </Select>
          </FormControl>
          <TextField
            variant="outlined"
            required
            label="Subject"
            value={value.subject}
            onChange={event => onChange({ ...value, subject: event.target.value })}
          />
          <TextField
            variant="outlined"
            label="CC recipients"
            helperText="Comma-separated email addresses"
            value={(value.ccRecipients || []).join(', ')}
            onChange={event => onChange({
              ...value,
              ccRecipients: event.target.value.split(',').map(item => item.trim()).filter(Boolean),
            })}
          />
          <TextField
            variant="outlined"
            required
            multiline
            minRows={16}
            label="Custom HTML"
            value={value.body}
            onChange={event => onChange({ ...value, body: event.target.value })}
          />
          <Typography variant="caption" color="textSecondary">
            Placeholders: {'{{name}}'}, {'{{company}}'}, {'{{country}}'}, {'{{currentMrr}}'}, {'{{renewalDate}}'}, {'{{email}}'}, {'{{temporaryPassword}}'}, {'{{loginUrl}}'}, {'{{intakeUrl}}'}, {'{{expiryDate}}'}. Customer values are HTML-escaped. Onboarding messages always include the credential and password-change block shown in the preview.
          </Typography>
        </Box>
        <Box>
          <FormControl fullWidth variant="outlined" style={{ marginBottom: 16 }}>
            <InputLabel>Preview customer</InputLabel>
            <Select
              label="Preview customer"
              value={value.testCustomerId || ''}
              onChange={event => onChange({ ...value, testCustomerId: event.target.value })}
            >
              <MenuItem value="">Example data</MenuItem>
              {customers.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="textSecondary">SUBJECT</Typography>
              {previewLoading
                ? <Box py={4}><Typography color="textSecondary">Loading contact and renewal data for this customer...</Typography></Box>
                : previewError
                  ? <Box py={2}><Typography color="error" role="alert">{previewError}</Typography></Box>
                  : <>
                    <Typography variant="subtitle1" gutterBottom>
                      {render(value.subject, customer, false)}
                    </Typography>
                    <iframe
                      title="Rendered email preview"
                      srcDoc={render(previewBody, customer, true)}
                      sandbox=""
                      className={classes.preview}
                    />
                  </>}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button
        color="primary"
        variant="contained"
        disabled={!value.subject.trim() || !value.body.trim()}
        onClick={onSave}
      >
        Save as active version
      </Button>
    </DialogActions>
  </Dialog>;
}
